/* eslint-disable react/no-unstable-nested-components */
import React, { useEffect, useState } from 'react';
import { Box } from '@chakra-ui/react';
import { DataGrid } from '../../components/datagrid';
import { SearchFilter } from '../../components/inputs/filters/search';
import { PageTitle } from '../../components/PageTitle';
import { isRequestFailure, PaginationType, useFetch } from '../../hooks/useFetch';
import { getCellStatus } from '../../utils/FormatFunctions';
import { SelectFilter } from '../../components/inputs/filters/select';

interface TeamOption {
  label: string;
  value: number;
}

const TEAM_PAGE_SIZE = 30;

export function StudentsPage() {
  const baseApiUrl = '/v1/admin';
  const baseUrl = `${baseApiUrl}/children`;
  const { requestApi, data, loading, error } = useFetch(baseUrl);
  const { requestApi: reqApiTeam, loading: teamsLoading } = useFetch(`${baseApiUrl}/teams`);
  const [teamOptions, setTeamOptions] = useState<TeamOption[]>([]);
  const [teamPagination, setTeamPagination] = useState<PaginationType | null>(null);
  const [teamRequest, setTeamRequest] = useState({ page: 1, search: '' });
  const columns = [
    { name: 'id', label: 'Id' },
    { name: 'name', label: 'Nome' },
    { name: 'team.name', label: 'Turma' },
    { name: 'status', label: 'Status' },
  ];

  useEffect(() => {
    let active = true;
    const timeout = setTimeout(
      async () => {
        const response = await reqApiTeam('get', {
          params: {
            page: teamRequest.page,
            per_page: TEAM_PAGE_SIZE,
            search: teamRequest.search,
          },
        });

        if (!active || isRequestFailure(response)) return;

        const nextOptions = (response.data?.data ?? []).map((item) => ({
          label: item.name,
          value: item.id,
        }));

        setTeamOptions((currentOptions) => {
          if (teamRequest.page === 1) return nextOptions;

          const optionsById = new Map(
            [...currentOptions, ...nextOptions].map((option) => [option.value, option])
          );
          return Array.from(optionsById.values());
        });
        setTeamPagination(response.data?.pagination ?? null);
      },
      teamRequest.search ? 250 : 0
    );

    return () => {
      active = false;
      clearTimeout(timeout);
    };
  }, [teamRequest]);

  return (
    <Box>
      <PageTitle description="Consulte os alunos matriculados e as turmas de cada um.">
        Alunos
      </PageTitle>

      <DataGrid
        columns={columns}
        rows={data?.data}
        requestApi={requestApi}
        error={error}
        baseUrl={baseUrl}
        data={data}
        hasCreate
        loading={loading}
        rowActions={{ show: false, edit: true, delete: true }}
        formatColum={[
          {
            field: 'status',
            formatField: getCellStatus,
          },
        ]}
        headerFilters={[
          {
            name: 'search',
            filterComponent: SearchFilter,
          },
          {
            name: 'team_id',
            filterComponent: SelectFilter,
            placeholder: 'Selecionar turma',
            isSearchable: true,
            isLoading: teamsLoading,
            loadingMessage: () => 'Carregando...',
            classNamePrefix: 'team-filter',
            onInputChange: (value, action) => {
              if (action.action !== 'input-change') return;
              setTeamOptions([]);
              setTeamPagination(null);
              setTeamRequest({ page: 1, search: value });
            },
            onMenuScrollToBottom: () => {
              if (!teamPagination?.has_next || teamsLoading) return;
              const nextPage = teamPagination.next_page ?? teamPagination.current_page + 1;
              setTeamRequest((currentRequest) =>
                currentRequest.page >= nextPage
                  ? currentRequest
                  : { ...currentRequest, page: nextPage }
              );
            },
            options: teamOptions,
          },
        ]}
      />
    </Box>
  );
}
